import dayjs from "dayjs";

import { CRM_STATISTICS_STORAGE_KEYS } from "../constants/crmStatisticsStorageKeys";
import { safeJsonParse, simulateNetworkDelay } from "../helpers/crmStatisticsServiceUtils";
import type {
  CrmMessageStatementItem,
  CrmMessageUsageStats,
  CrmQuestionnaireLatestResponseItem,
  CrmQuestionnaireUsageStats,
  CrmWhatsappAccountConfig,
} from "../types";

type StorageEnvelope<T> = {
  updatedAtIso: string;
  data: T;
};

const DEFAULT_MESSAGE_STATS: CrmMessageUsageStats = {
  totalMessagesContracted: 1000,
  totalMessagesUsed: 320,
  totalMessagesAvailable: 680,
  remainingMessagesForSend: 360,
  usageRateInPercentage: 32,
};

const DEFAULT_MESSAGE_STATEMENT: CrmMessageStatementItem[] = [
  {
    id: "statement-1",
    description: "Lote de confirmação de consultas de amanhã",
    channelLabel: "WhatsApp",
    quantity: 32,
    status: "sent",
    createdAtIso: dayjs().subtract(1, "day").toISOString(),
  },
  {
    id: "statement-2",
    description: "Pós-consulta + pesquisa de satisfação",
    channelLabel: "WhatsApp",
    quantity: 18,
    status: "scheduled",
    createdAtIso: dayjs().subtract(6, "hour").toISOString(),
  },
  {
    id: "statement-3",
    description: "Lembrete de pagamento pendente",
    channelLabel: "WhatsApp",
    quantity: 7,
    status: "sent",
    createdAtIso: dayjs().subtract(2, "day").toISOString(),
  },
  {
    id: "statement-4",
    description: "Triagem para novos pacientes",
    channelLabel: "WhatsApp",
    quantity: 12,
    status: "failed",
    createdAtIso: dayjs().subtract(3, "day").toISOString(),
  },
];

const DEFAULT_WHATSAPP_ACCOUNT_CONFIG: CrmWhatsappAccountConfig = {
  wabaPhoneNumber: "+55 11 99999-0000",
  whatsappPhoneNumber: "+55 11 98888-7777",
  metaBusinessAccountId: "act_123456789012345",
  connectionStatusLabel: "Conectado (mock)",
};

const DEFAULT_QUESTIONNAIRE_STATS: CrmQuestionnaireUsageStats = {
  totalSends: 86,
  totalResponses: 65,
  averageResponseRateInPercentage: 76,
};

const DEFAULT_LATEST_QUESTIONNAIRE_RESPONSES: CrmQuestionnaireLatestResponseItem[] = [
  {
    id: "latest-1",
    patientName: "Maria Lima",
    questionnaireTitle: "Pré-consulta (história clínica)",
    receivedRelativeDescription: "Há 15 min",
    highlightText: "Dor principal: cefaleia há 3 dias, pior à noite.",
  },
  {
    id: "latest-2",
    patientName: "Ana Ribeiro",
    questionnaireTitle: "Triagem rápida",
    receivedRelativeDescription: "Há 1h",
    highlightText: "Sinal de alerta: febre persistente relatada.",
  },
  {
    id: "latest-3",
    patientName: "Fernando Costa",
    questionnaireTitle: "Pré-consulta (história clínica)",
    receivedRelativeDescription: "Hoje cedo",
    highlightText: "Medicações em uso: relata anti-inflamatório há 2 dias.",
  },
];

function readEnvelope<T>(storageKey: string): StorageEnvelope<T> | null {
  return safeJsonParse<StorageEnvelope<T>>(localStorage.getItem(storageKey));
}

function persistEnvelope<T>(storageKey: string, data: T): void {
  const payload: StorageEnvelope<T> = {
    updatedAtIso: dayjs().toISOString(),
    data,
  };

  localStorage.setItem(storageKey, JSON.stringify(payload));
}

async function getMessageUsageStats(): Promise<CrmMessageUsageStats> {
  await simulateNetworkDelay(350);
  const stored = readEnvelope<CrmMessageUsageStats>(CRM_STATISTICS_STORAGE_KEYS.messageStats);
  return stored?.data ?? DEFAULT_MESSAGE_STATS;
}

async function getMessageStatement(): Promise<CrmMessageStatementItem[]> {
  await simulateNetworkDelay(450);
  const stored = readEnvelope<CrmMessageStatementItem[]>(
    CRM_STATISTICS_STORAGE_KEYS.messageStatement,
  );

  return stored?.data ?? DEFAULT_MESSAGE_STATEMENT;
}

async function getWhatsappAccountConfig(): Promise<CrmWhatsappAccountConfig> {
  await simulateNetworkDelay(350);
  const stored = readEnvelope<CrmWhatsappAccountConfig>(
    CRM_STATISTICS_STORAGE_KEYS.whatsappAccountConfig,
  );

  return stored?.data ?? DEFAULT_WHATSAPP_ACCOUNT_CONFIG;
}

async function updateWhatsappAccountConfig(
  input: CrmWhatsappAccountConfig,
): Promise<CrmWhatsappAccountConfig> {
  await simulateNetworkDelay(550);
  persistEnvelope(CRM_STATISTICS_STORAGE_KEYS.whatsappAccountConfig, input);
  return input;
}

async function getQuestionnaireUsageStats(): Promise<CrmQuestionnaireUsageStats> {
  await simulateNetworkDelay(350);
  const stored = readEnvelope<CrmQuestionnaireUsageStats>(
    CRM_STATISTICS_STORAGE_KEYS.questionnaireStats,
  );

  return stored?.data ?? DEFAULT_QUESTIONNAIRE_STATS;
}

async function getLatestQuestionnaireResponses(): Promise<
  CrmQuestionnaireLatestResponseItem[]
> {
  await simulateNetworkDelay(450);
  // Nesta fase não persistimos; é suficiente para storytelling visual do protótipo.
  return DEFAULT_LATEST_QUESTIONNAIRE_RESPONSES;
}

// Exportamos um objeto "service" para manter a API consistente com outros módulos.
export const crmStatisticsService = {
  getMessageUsageStats,
  getMessageStatement,
  getWhatsappAccountConfig,
  updateWhatsappAccountConfig,
  getQuestionnaireUsageStats,
  getLatestQuestionnaireResponses,
};

